<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { useStore } from "vuex"
import { useI18n } from "vue-i18n"
import {ref, watch, computed, onMounted, nextTick, onUnmounted} from 'vue';
import { showSnackbar } from "@/helper"
import { useBreakpoint } from "./composables/useBreakpoint"
import { useCart } from "./composables/useCart"
import { usePayment, paymentManager, isProceedErrorRedirect } from "@/composables/usePayment"
import { useFreeSamples } from "./composables/useFreeSamples"
import { defineAsyncComponent } from "vue"
import { useUser } from "@/app/Checkout/composables/useUser"
import FreeSamplesModal from "./components/FreeSamplesModal/index.vue"
import VoucherModal from "./components/VoucherModal/index.vue"
import { useAddresses } from "@/app/Checkout/composables/useAddresses"
import MobileComponent from "./views/Mobile/index.vue"
import DesktopComponent from "./views/Desktop/index.vue"
import { useAuth } from "@/app/Checkout/composables/useAuth"
import { useCheckout } from "@/app/Checkout/composables/useCheckout"
import { initAdyen, handleAdditionalDetails, setAdyenValid, setAdyenHasInput, resetAdyenErrors, setFormSubmitPressed } from "@/composables/useAdyen"

const store = useStore()
const { t } = useI18n()
const { cart } = useCart()
const { getUser } = useUser()
const adyenCheckout = ref(null)
const { isLoggedIn } = useAuth()
const { isMobile } = useBreakpoint()
const { pendingAddress } = useAddresses()
const { checkout, activePaymentMethod } = useCheckout()
const { updateSelectedSamplesFromCart, incrementFreeSamplesModalKey } = useFreeSamples()
const { adyenConfig, getAdyenConfig, getAmazonPayConfig, proceedToPayment, handleErrorParamFlashMessage } = usePayment()
const pendingCart = computed(() => store.state.cart.pending)
const pendingCheckout = computed(() => store.state.checkout.pending)

// Free samples modal – visibility from store so button in Aside (inside async Desktop) can open it
const isFreeSamplesModalVisible = computed(() => Boolean(store.state.product?.isFreeSamplesModalVisible))
const freeSamplesModalKey = computed(() => store.state.product?.freeSamplesModalKey ?? 0)

const isExternalReferrer = () => {
  const referrer = window.document.referrer
  if (!referrer) return true

  try {
    const referrerUrl = new URL(referrer)
    const currentUrl = new URL(window.location.href)
    return referrerUrl.hostname !== currentUrl.hostname
  } catch (e) {
    return false
  }
}

const handleLoopProtection = () => {
  const currentUrl = window.location.href
  if (isExternalReferrer()) {
    sessionStorage.setItem('last_visited_url', currentUrl)
  } else {
    const lastStoredUrl = sessionStorage.getItem('last_visited_url')
    if (lastStoredUrl) {
      sessionStorage.removeItem('last_visited_url')
    }
    if (lastStoredUrl === currentUrl) {
      window.history.back()
    }
  }
}

function onFreeSamplesModalUpdate(visible) {
  if (!visible) {
    store.commit("product/SET_FREE_SAMPLES_MODAL_VISIBLE", false)
  }
}

// Voucher modal – visibility from store so CartVoucherBlock (in Aside) can open it
const isVoucherModalVisible = computed(() => {
  const v = store.state.vouchers
  return Boolean(v && v.isVoucherModalVisible)
})

function onVoucherModalUpdate(visible) {
  if (!visible) {
    store.commit('vouchers/SET_VOUCHER_MODAL_VISIBLE', false)
    store.commit('vouchers/REMOVE_ERROR')
  }
}

watch(cart, (newCart) => {
  if (newCart) {
    updateSelectedSamplesFromCart(newCart)
  }
  const itemCount = newCart?.cart_items?.length ?? 0
  if (itemCount === 0) {
    incrementFreeSamplesModalKey()
  }
}, { immediate: true })

onMounted(async () => {
  window.addEventListener('pageshow', handleLoopProtection)
  if (!checkout.value && !pendingCheckout.value) {
    await store.dispatch("checkout/getCheckout")
  }

   const loadPromises = [
    store.dispatch("eVouchers/getCardStyles"),
    store.dispatch("product/getFreeSamples"),
    store.dispatch("user/getAddresses"),
    store.dispatch("auth/getAuthStatus"),
    store.dispatch("country/getCountries"),
    store.dispatch("user/getCountryPhoneCodes"),
    store.dispatch("payment/getPaymentMethods"),
    getAdyenConfig(),
  ]

  if (activePaymentMethod.value === 21) {
    loadPromises.push(getAmazonPayConfig())
  }

  await Promise.all(loadPromises)

  if (isLoggedIn.value) {
    await getUser()
  }

  const queryParams = new URLSearchParams(window.location.search)
  const errorParam = queryParams.get('err')
  if (errorParam) {
    handleErrorParamFlashMessage(window.location.href)
  }

  if (!adyenCheckout.value) {
    const config = adyenConfig.value

    adyenCheckout.value = await initAdyen({
      environment: config.env,
      clientKey: config.client_key,
      locale: config.locale,
      countryCode: config.countryCode,
      onAdditionalDetails: handleAdditionalDetails,
      onError: error => {
        if (error?.errorCode !== 'canceledByShopper') {
          showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.default_payment_error') || t('messages.page.revocation.error'))
        }
      },
      onChange: state => {
        const hasInput = Boolean(Object.keys(state.valid).length)
        setAdyenValid(state.isValid)
        setAdyenHasInput(hasInput)
        if (state.isValid && hasInput) {
          setFormSubmitPressed(false)
        }
      },
      amount: {
        currency: cart.value?.currency,
        value: cart.value?.total_price_cents,
      },
      onSubmit: async (state, component) => {
        if (!state.isValid) {
          setFormSubmitPressed(true)
          showSnackbar('error', 'iconfont-info', t('messages.page.revocation.error'))
          return
        }

        try {
          const response = await proceedToPayment(state.data)

          if (!response) {
            showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.default_payment_error') || t('messages.page.revocation.error'))
            throw new Error('Payment API error - no response')
          }

          // Backend returns url/redirect with ?err=… (may be err%3D…) — snackbar is shown in proceedToPayment
          if (isProceedErrorRedirect(response)) {
            await nextTick()
            throw new Error('Payment error')
          }

          if (response?.action) {
            component.handleAction(response.action)
          } else if (response?.result?.resultCode) {
            switch (response.result.resultCode) {
              case 'Authorised':
                await paymentManager.confirm(response)
                break
              case 'Refused':
                showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.payment_refused') || t('messages.page.revocation.error'))
                throw new Error('Payment refused')
              case 'Cancelled':
                throw new Error('Payment cancelled')
              default:
                showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.default_payment_error') || t('messages.page.revocation.error'))
                throw new Error('Payment error')
            }
          } else {
            await paymentManager.confirm(response)
          }
        } catch (error) {
          const knownErrors = ['Payment refused', 'Payment cancelled', 'Payment error']
          if (!knownErrors.includes(error?.message)) {
            showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.default_payment_error') || t('messages.page.revocation.error'))
          }
        }
      },
    })
  }
})

onUnmounted(() => {
  window.removeEventListener('pageshow', handleLoopProtection)
  resetAdyenErrors()
})
</script>
